import { Check, ChevronDown, ChevronUp, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { formatCurrency } from '../utils/purchaseOrder.utils'

import { FormField } from './FormField'

const GST_TYPE_OPTIONS = [
  {
    value: 'CGST_SGST',
    label: 'CGST + SGST',
  },
  {
    value: 'IGST',
    label: 'IGST',
  },
]

const ZERO_TAX_TYPES = []

export function PurchaseOrderItemRow({
  index,
  item,
  onChange,
  onRemove,
  canRemove,
}) {
  const [isGstDropdownOpen, setIsGstDropdownOpen] = useState(false)

  const qty = Number(item.quantity) || 0
  const rate = Number(item.unitPrice) || 0

  const isZeroTaxType = ZERO_TAX_TYPES.includes(item.gstType)

  const gst = isZeroTaxType ? 0 : Number(item.gstValue) || 0

  const baseTotal = qty * rate
  const gstAmount = (baseTotal * gst) / 100
  const grandLineTotal = baseTotal + gstAmount

  const selectedGstOption = GST_TYPE_OPTIONS.find(
    (option) => option.value === item.gstType,
  )

  const taxValueLabel =
    item.gstType === 'CGST_SGST'
      ? 'CGST + SGST Value (%)'
      : item.gstType === 'IGST'
        ? 'IGST Value (%)'
        : 'Tax Value (%)'

  const handleGstTypeChange = (value) => {
    onChange(index, {
      target: {
        name: 'gstType',
        value,
      },
    })

    if (ZERO_TAX_TYPES.includes(value)) {
      onChange(index, {
        target: {
          name: 'gstValue',
          value: '0',
        },
      })
    }

    setIsGstDropdownOpen(false)
  }

  return (
    <div className="group relative rounded-2xl border border-slate-200 bg-white p-4.5 shadow-xs transition hover:border-slate-300">
      <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
            {index + 1}
          </span>

          <span className="text-sm font-semibold text-slate-800">
            Product Item {index + 1}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {baseTotal > 0 && (
            <span className="text-xs font-medium text-slate-500">
              Line Total:{' '}
              <span className="font-semibold text-slate-900">
                {formatCurrency(grandLineTotal)}
              </span>
            </span>
          )}

          {canRemove && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 hover:text-red-700"
              title="Remove item"
            >
              <Trash2 className="size-3.5" />
              <span>Remove</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <FormField
          label="Product / Item Detail"
          name="detail"
          value={item.detail}
          onChange={(e) => {
            const sanitized = e.target.value.replace(
              /[^a-zA-Z0-9\s.,'()-]/g,
              '',
            )
            onChange(index, {
              target: {
                name: 'detail',
                value: sanitized,
              },
            })
          }}
          placeholder="e.g. Industrial bearings"
          required
        />

        <FormField
          label="HSN Code"
          name="hsnCode"
          value={item.hsnCode}
          onChange={(e) => {
            const numericValue = e.target.value.replace(/\D/g, '')
            onChange(index, {
              target: {
                name: 'hsnCode',
                value: numericValue,
              },
            })
          }}
          placeholder="e.g. 848271"
          required
        />

        <FormField
          label="Quantity"
          name="quantity"
          type="number"
          min="0.01"
          step="any"
          value={item.quantity}
          onChange={(e) => onChange(index, e)}
          placeholder="1"
          required
        />

        <FormField
          label="Unit Price (₹)"
          name="unitPrice"
          type="number"
          min="0"
          step="any"
          value={item.unitPrice}
          onChange={(e) => onChange(index, e)}
          placeholder="0.00"
          required
        />

        {/* GST Type */}
        <div className="relative">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-700">
            GST Type <span className="text-red-500">*</span>
          </label>

          <button
            type="button"
            onClick={() => setIsGstDropdownOpen((prev) => !prev)}
            className={`flex w-full items-center justify-between rounded-xl border bg-white px-3.5 py-2.5 text-left text-sm transition-all ${
              isGstDropdownOpen
                ? 'border-[#080d25] ring-2 ring-[#080d25]/10'
                : 'border-slate-200'
            }`}
          >
            <span
              className={
                selectedGstOption ? 'text-slate-900' : 'text-slate-400'
              }
            >
              {selectedGstOption?.label || 'Select GST Type'}
            </span>

            {isGstDropdownOpen ? (
              <ChevronUp className="size-4 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="size-4 shrink-0 text-slate-500" />
            )}
          </button>

          {isGstDropdownOpen && (
            <div className="absolute left-0 top-full z-[100] mt-2 w-[320px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              <div className="max-h-[300px] overflow-y-auto pr-1">
                {GST_TYPE_OPTIONS.map((option) => {
                  const isSelected = item.gstType === option.value

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleGstTypeChange(option.value)}
                      className={`flex w-full items-start gap-3 rounded-xl px-4 py-3 text-left text-sm transition-colors ${
                        isSelected
                          ? 'bg-[#e8ecf8] text-[#243b8f]'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center">
                        {isSelected && (
                          <Check className="size-4 text-[#3157c7]" />
                        )}
                      </span>

                      <span className="min-w-0 flex-1 whitespace-normal break-words leading-6">
                        {option.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* GST / Tax Value */}
        {item.gstType && (
          <FormField
            label={taxValueLabel}
            name="gstValue"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={isZeroTaxType ? '0' : item.gstValue}
            onChange={(e) => {
              if (isZeroTaxType) return

              onChange(index, e)
            }}
            placeholder="e.g. 18"
            required
            disabled={isZeroTaxType}
          />
        )}
      </div>
    </div>
  )
}
